"use client";

import { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import axios from "axios";
import { API_BASE_URL } from '@/app/lib/api';

// Définition du type pour correspondre à votre API
interface PromoMessage {
  id: string;
  message: string;
  dateDebut: string;
  dateFin: string;
}

export default function SaleBanner() {
  const [activeMessages, setActiveMessages] = useState<PromoMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAndFilterMessages = async () => {
      try {
        // 1. Récupérer les messages depuis votre API
        const apiUrl = `${API_BASE_URL}/promo-message`;
        const response = await axios.get<PromoMessage[]>(apiUrl);
        const allMessages = response.data;

        // 2. Filtrer pour ne garder que les messages actifs
        const now = new Date();
        const filteredMessages = allMessages.filter(msg => {
          const startDate = new Date(msg.dateDebut);
          const endDate = new Date(msg.dateFin);
          // Important: on met l'heure de fin à 23:59:59 pour inclure toute la journée
          endDate.setHours(23, 59, 59, 999);

          return now >= startDate && now <= endDate;
        });

        setActiveMessages(filteredMessages);

      } catch (error) {
        console.error("Failed to load promotional banner:", error);
        // En cas d'erreur, on n'affiche simplement rien.
        setActiveMessages([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndFilterMessages();
  }, []); // Le tableau vide [] assure que l'effet ne s'exécute qu'une fois

  // 3. Ne rien afficher pendant le chargement ou s'il n'y a aucun message actif
  if (isLoading || activeMessages.length === 0) {
    return null; // Retourne null pour ne pas afficher une barre noire vide
  }

  // 4. Afficher le carrousel avec les messages actifs
  return (
    <div className="bg-black text-white h-[40px] flex items-center">
      <div className="container mx-auto">
        <div className="flex justify-center">
          <div className="w-full lg:w-2/3 text-center">
            <div className="relative overflow-hidden">
              <Swiper
                modules={[Navigation, Autoplay]}
                navigation={{
                  nextEl: '.swiper-button-next-custom',
                  prevEl: '.swiper-button-prev-custom',
                }}
                spaceBetween={0}
                slidesPerView={1}
                loop={activeMessages.length > 1}
                autoplay={{ delay: 5000, disableOnInteraction: false }} // 5 secondes par message
                className="tf-sw-top_bar"
              >
                {activeMessages.map((promo) => (
                  <SwiperSlide key={promo.id}>
                    <p className="font-regular text-sm md:text-base tracking-wider truncate whitespace-nowrap px-10">
                      {promo.message}
                    </p>
                  </SwiperSlide>
                ))}
              </Swiper>

              {/* Flèches de navigation personnalisées */}
              <div className="swiper-button-prev-custom absolute top-1/2 -translate-y-1/2 left-2 cursor-pointer z-10">
                <svg width="20" height="20" viewBox="0 0 27 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0.292892 6.79289C-0.0976315 7.18342 -0.0976315 7.81658 0.292892 8.20711L6.65685 14.5711C7.04738 14.9616 7.68054 14.9616 8.07107 14.5711C8.46159 14.1805 8.46159 13.5474 8.07107 13.1569L2.41421 7.5L8.07107 1.84315C8.46159 1.45262 8.46159 0.819457 8.07107 0.428932C7.68054 0.0384078 7.04738 0.0384078 6.65685 0.428932L0.292892 6.79289ZM27 6.5L1 6.5V8.5L27 8.5V6.5Z" fill="white" />
                </svg>
              </div>
              <div className="swiper-button-next-custom absolute top-1/2 -translate-y-1/2 right-2 cursor-pointer z-10">
                <svg width="20" height="20" viewBox="0 0 27 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M26.7071 8.20711C27.0976 7.81658 27.0976 7.18342 26.7071 6.79289L20.3431 0.428932C19.9526 0.0384078 19.3195 0.0384078 18.9289 0.428932C18.5384 0.819457 18.5384 1.45262 18.9289 1.84315L24.5858 7.5L18.9289 13.1569C18.5384 13.5474 18.5384 14.1805 18.9289 14.5711C19.3195 14.9616 19.9526 14.9616 20.3431 14.5711L26.7071 8.20711ZM0 8.5L26 8.5V6.5L0 6.5L0 8.5Z" fill="white" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}